<template>
  <div class="page">
    <div class="card">
      <h2>{{ cfg.ui.title }}</h2>
      <p class="subtitle">
        {{ cfg.ui.subtitle }}
      </p>

      <!-- 登入區 -->
      <div v-if="!loggedIn" class="form">
        <label class="field">
          後台密碼（本機預設 dev-secret）：
          <input
            type="password"
            v-model="adminToken"
            placeholder="請輸入後台密碼"
          />
        </label>
        <button @click="login">登入後台</button>
        <p v-if="loginError" class="error">{{ loginError }}</p>
      </div>

      <!-- 登入後的主畫面：上方分頁，下方依分頁顯示內容 -->
      <div v-else class="form">
        <!-- 分頁切換 -->
        <div class="tabs">
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'unavailable' }"
            @click="switchTab('unavailable')"
          >
            不開放設定
          </button>
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'bookings' }"
            @click="switchTab('bookings')"
          >
            預約管理
          </button>
          <!-- 
            ✅ 點「資料庫管理」分頁時，順便抓列表
          -->
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'userAccess' }"
            @click="switchTab('userAccess') && fetchUserAccessList()"
          >
            資料庫管理
          </button>
          <!-- 
          ==========================================================
          ✅ 系統日誌
          ==========================================================
          -->
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'logs' }"
            @click="switchTab('logs')"
          >
            系統日誌
          </button>

          <!-- 
          ==========================================================
          ✅ 分頁五：Prompt 管理（MiniBazi 先做 4 檔）
          - 目的：讓你在前端改 prompt、保存、備份、匯出
          - 最小改動：直接在 AdminPanel.vue 做，不抽 composable
          ==========================================================
          -->
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'prompts' }"
            @click="
              switchTab('prompts') &&
                fetchMiniBaziPrompts() /* ✅ 點進去就抓一次最新 */
            "
          >
            Prompt 管理
          </button>
          <!-- 
          ==========================================================
          ✅ 分頁六：文章管理（Articles）
          - 目的：先做到「看得到列表 + 可用 tag/status/q 篩選」
          - 下一步才會做：新增/編輯/刪除 + Tiptap
          ==========================================================
          -->
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'articles' }"
            @click="switchTab('articles')"
          >
            文章管理
          </button>

          <!--
          ==========================================================
          ✅ 分頁七：八字審定資料（MiniBazi Advice）
          - 編輯日主 × 月支 的命理師審定文字
          - 內容會 inject 進 minibazi AI prompt
          ==========================================================
          -->
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'minibazi_advice' }"
            @click="switchTab('minibazi_advice')"
          >
            八字審定資料
          </button>

          <!--
          ==========================================================
          ✅ 分頁八：六爻卜卦紀錄（LiuYao Records）
          - 檢視每次六爻卜卦的完整資料（含 AI 解卦）
          - 老師可填占卦者名字、標記已驗證、寫心得
          ==========================================================
          -->
          <button
            class="tab-btn"
            :class="{ active: activeTab === 'liuyao_records' }"
            @click="switchTab('liuyao_records')"
          >
            六爻紀錄
          </button>
        </div>

        <!-- ========== 分頁一：不開放設定（月曆格，已拆出去） ========== -->
        <UnavailableCalendarPanel
          v-if="activeTab === 'unavailable'"
          class="tab-panel"
          :unavailable="unavailable"
          :bookings="bookings"
          :allTimeSlots="allTimeSlots"
          :services="services"
          @save="saveUnavailableToServer"
          @dirty-change="unavailableDirty = $event"
        />

        <!-- ========== 分頁二：預約管理（含行事曆） ========== -->
        <div v-else-if="activeTab === 'bookings'" class="tab-panel">
          <!-- 行事曆 -->
          <div class="calendar-panel">
            <div class="calendar-header">
              <button @click="prevMonth">‹</button>
              <span>{{ calendarYear }} 年 {{ calendarMonth }} 月</span>
              <button @click="nextMonth">›</button>
            </div>

            <table class="calendar-table">
              <thead>
                <tr>
                  <th>日</th>
                  <th>一</th>
                  <th>二</th>
                  <th>三</th>
                  <th>四</th>
                  <th>五</th>
                  <th>六</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(week, wIndex) in calendarWeeks" :key="wIndex">
                  <td
                    v-for="(cell, dIndex) in week"
                    :key="dIndex"
                    class="calendar-cell"
                    :class="{
                      'is-today': cell && cell.dateStr === todayStr,
                      'has-bookings': cell && cell.hasBookings,
                      'is-blocked-day': cell && cell.isFullDayBlocked,
                      'is-selected': cell && cell.dateStr === selectedDate,
                    }"
                    @click="selectDateFromCalendar(cell)"
                  >
                    <div v-if="cell" class="cell-inner">
                      <div class="day-number">{{ cell.day }}</div>
                      <div class="cell-tags">
                        <span
                          v-if="cell.isFullDayBlocked"
                          class="tag blocked-tag"
                        >
                          休
                        </span>
                        <span v-else-if="cell.hasBookings" class="tag open-tag">
                          約
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <div class="calendar-legend">
              <span><span class="legend-box legend-open"></span> 有預約</span>
              <span
                ><span class="legend-box legend-blocked"></span>
                整天不開放</span
              >
              <span v-if="selectedDate">
                目前選擇日期：<strong>{{ selectedDate }}</strong>
              </span>
              <span v-else> 點選上方行事曆中的日期，查看當天預約。 </span>
            </div>
          </div>

          <!-- 預約列表 -->
          <div class="toolbar">
            <button @click="fetchBookings">重新載入預約列表</button>
          </div>

          <div v-if="loadingBookings">
            <em>載入中...</em>
          </div>

          <div v-else-if="bookingsForSelectedDate.length === 0">
            <em v-if="selectedDate"> 此日期目前沒有預約。 </em>
            <em v-else> 尚無任何預約，或是尚未選擇日期。 </em>
          </div>

          <div v-else class="table-wrapper">
            <table class="booking-table">
              <thead>
                <tr>
                  <th>日期</th>
                  <th>時段</th>
                  <th>項目</th>
                  <th>姓名</th>
                  <th>聯絡方式</th>
                  <th>狀態</th>
                  <th>建立時間</th>
                  <th>操作頁</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="b in bookingsForSelectedDate" :key="b.id">
                  <td>{{ b.date }}</td>
                  <td>
                    <div class="timechips">
                      <span
                        v-for="slot in normalizeSlots(b)"
                        :key="slot"
                        class="chip"
                      >
                        {{ slot }}
                      </span>
                    </div>
                  </td>
                  <td>{{ serviceTitle(b.serviceId) }}</td>
                  <td>{{ b.name }}</td>
                  <td class="contact-cell">{{ b.contact }}</td>
                  <td>
                    <select
                      v-model="b.status"
                      @change="updateStatus(b)"
                      class="status-select"
                    >
                      <option value="pending">待處理</option>
                      <option value="done">已完成</option>
                      <option value="canceled">已取消</option>
                    </select>
                  </td>
                  <td>{{ formatDateTime(b.createdAt) }}</td>
                  <td>
                    <button class="danger-btn" @click="removeBooking(b)">
                      刪除
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- ========== 分頁三：資料庫管理（user_access） ========== -->
        <div v-else-if="activeTab === 'userAccess'" class="tab-panel">
          <!-- 
            ✅ 先做最小可用：列表顯示
            - 搜尋/分頁下一步再加
          -->
          <!-- 
            ==========================================
            user_access - 工具列（搜尋 / 分頁 / 重新載入）
            ==========================================
            ✅ 為什麼先做這個：
            - 先把「查得到、翻得到」做出來
            - 編輯/新增/刪除下一步再加，避免一次太多
          -->
          <div class="toolbar">
            <!-- ✅ 搜尋框 -->
            <div class="ua-search">
              <input
                v-model.trim="userAccessQ"
                type="text"
                placeholder="搜尋 user_id"
                @keyup.enter="onUserAccessSearch"
              />
              <button @click="onUserAccessSearch">搜尋</button>
              <button class="ghost-btn" @click="clearUserAccessSearch">
                清除
              </button>
            </div>

            <!-- ✅ 右側操作 -->
            <div class="ua-actions">
              <button @click="fetchUserAccessList">重新載入</button>
            </div>
          </div>

          <!-- ✅ 分頁列 -->
          <div class="ua-pagination">
            <button
              class="ghost-btn"
              :disabled="userAccessLoading || userAccessPage <= 1"
              @click="prevUserAccessPage"
            >
              上一頁
            </button>

            <!-- 
              ✅ 顯示：第 X 頁 / 共 Y 頁（共 N 筆）
            -->
            <span class="ua-pageinfo">
              第 <strong>{{ userAccessPage }}</strong> 頁 / 共
              <strong>{{ userAccessLastPage }}</strong> 頁
              <span class="ua-total">（共 {{ userAccessTotal }} 筆）</span>
            </span>

            <button
              class="ghost-btn"
              :disabled="userAccessLoading || isUserAccessLastPage"
              @click="nextUserAccessPage"
            >
              下一頁
            </button>
          </div>

          <div v-if="userAccessLoading">
            <em>載入中...</em>
          </div>

          <div v-else-if="userAccessItems.length === 0">
            <em>目前沒有資料（或這一頁沒有資料）。</em>
          </div>

          <div v-else class="table-wrapper">
            <table class="booking-table">
              <!-- 
                ==========================================
                user_access - 表頭（拆欄位）
                ==========================================
                ✅ 目的：
                - JSON 讀起來太卡
                - 拆成 6 欄後更像後台在管理資料
                - 下一步要做「直接編輯」也更好接
              -->
              <thead>
                <tr>
                  <th>user_id</th>

                  <!-- ✅ first_free 三欄 -->
                  <th class="num-cell">首免｜六爻</th>
                  <th class="num-cell">首免｜八字</th>
                  <th class="num-cell">首免｜合婚</th>

                  <!-- ✅ quota 三欄 -->
                  <th class="num-cell">配額｜六爻</th>
                  <th class="num-cell">配額｜八字</th>
                  <th class="num-cell">配額｜合婚</th>

                  <th class="num-cell">updated_at</th>
                  <!-- ✅ 操作欄：編輯/保存/取消 -->
                  <th class="num-cell">操作</th>
                </tr>
              </thead>
              <!-- 
                ==========================================
                user_access - 表身（拆欄位顯示）
                ==========================================
                ✅ 做法：
                - 用安全取值：如果 first_free/quota 沒資料就顯示 0
              -->
              <tbody>
                <tr v-for="u in userAccessItems" :key="u.user_id">
                  <td class="contact-cell">{{ u.user_id }}</td>

                  <!-- ✅ first_free -->
                  <td class="num-cell">
                    <template v-if="userAccessEditingId === u.user_id">
                      <input
                        class="num-input"
                        type="number"
                        v-model.number="editDraft.first_free.liuyao"
                      />
                    </template>
                    <template v-else>
                      {{ getNum(u.first_free, "liuyao") }}
                    </template>
                  </td>
                  <td class="num-cell">
                    <template v-if="userAccessEditingId === u.user_id">
                      <input
                        class="num-input"
                        type="number"
                        v-model.number="editDraft.first_free.minibazi"
                      />
                    </template>
                    <template v-else>
                      {{ getNum(u.first_free, "minibazi") }}
                    </template>
                  </td>
                  <td class="num-cell">
                    <template v-if="userAccessEditingId === u.user_id">
                      <input
                        class="num-input"
                        type="number"
                        v-model.number="editDraft.first_free.bazimatch"
                      />
                    </template>
                    <template v-else>
                      {{ getNum(u.first_free, "bazimatch") }}
                    </template>
                  </td>

                  <!-- ✅ quota -->
                  <td class="num-cell">
                    <template v-if="userAccessEditingId === u.user_id">
                      <input
                        class="num-input"
                        type="number"
                        v-model.number="editDraft.quota.liuyao"
                      />
                    </template>
                    <template v-else>
                      {{ getNum(u.quota, "liuyao") }}
                    </template>
                  </td>
                  <td class="num-cell">
                    <template v-if="userAccessEditingId === u.user_id">
                      <input
                        class="num-input"
                        type="number"
                        v-model.number="editDraft.quota.minibazi"
                      />
                    </template>
                    <template v-else>
                      {{ getNum(u.quota, "minibazi") }}
                    </template>
                  </td>
                  <td class="num-cell">
                    <template v-if="userAccessEditingId === u.user_id">
                      <input
                        class="num-input"
                        type="number"
                        v-model.number="editDraft.quota.bazimatch"
                      />
                    </template>
                    <template v-else>
                      {{ getNum(u.quota, "bazimatch") }}
                    </template>
                  </td>

                  <td>{{ formatDateTime(u.updated_at) }}</td>
                  <td class="ops-cell">
                    <!-- ✅ 非編輯狀態 -->
                    <template v-if="userAccessEditingId !== u.user_id">
                      <button class="ghost-btn" @click="startEditUserAccess(u)">
                        編輯
                      </button>

                      <!-- 
                        ✅ 刪除整筆
                        - 為什麼放這：只有非編輯狀態才允許刪除，避免誤刪
                      -->
                      <button
                        class="danger-btn"
                        @click="deleteUserAccess(u.user_id)"
                      >
                        刪除
                      </button>
                    </template>

                    <!-- ✅ 編輯狀態 -->
                    <template v-else>
                      <button
                        @click="saveEditUserAccess"
                        :disabled="userAccessSaving"
                      >
                        {{ userAccessSaving ? "保存中..." : "保存" }}
                      </button>
                      <button
                        class="ghost-btn"
                        @click="cancelEditUserAccess"
                        :disabled="userAccessSaving"
                      >
                        取消
                      </button>
                    </template>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <!-- 
        ==========================================================
        ✅ 分頁四：系統日誌（先做到「能抓回資料」）
        - 目的：先確認 fetchLogsList OK
        ==========================================================
        -->
        <div v-else-if="activeTab === 'logs'" class="tab-panel">
          <div class="toolbar">
            <!-- ✅ 手動測試用：按了就打 API -->
            <button @click="fetchLogsList">刷新日誌</button>

            <!-- ✅ 先顯示總筆數（確認 data.total 有回來） -->
            <span style="font-size: 12px; color: #666">
              第 {{ logsPage }} 頁｜共 {{ logsTotal }} 筆
            </span>
          </div>

          <div v-if="logsLoading">
            <em>載入中...</em>
          </div>

          <p v-else-if="logsError" class="error">{{ logsError }}</p>

          <div v-else>
            <!-- 
            ==========================================================
            ✅ Logs 表格：先顯示最必要欄位
            - 台灣時間 / level / tag / user_id / message
            ==========================================================
            -->
            <!-- 
            ==========================================================
            ✅ Logs：聊天室樣式（可捲動）
            - 目的：不用篩選也能舒服地看大量 logs
            - 做法：用一個固定高度的容器 + overflow-y: auto
            ==========================================================
            -->
            <div class="logs-chat">
              <!-- ✅ 沒資料時的提示 -->
              <div
                v-if="!logsItems || logsItems.length === 0"
                class="logs-empty"
              >
                <em>目前沒有日誌</em>
              </div>

              <!-- ✅ 有資料：一筆 log 一行 -->
              <div v-else class="logs-list">
                <div v-for="l in logsItems" :key="l.id" class="log-row">
                  <!-- ✅ 左側：user / level / tag -->
                  <div class="log-meta">
                    <div class="log-user">{{ l.user_id || "-" }}</div>
                    <div class="log-badges">
                      <span class="badge" :class="`badge-level-${l.level}`">{{
                        l.level
                      }}</span>
                      <span class="badge" :class="`badge-tag-${l.tag}`">{{
                        l.tag
                      }}</span>
                    </div>
                  </div>

                  <!-- ✅ 中間：message -->
                  <div class="log-msg">
                    {{ l.message }}
                  </div>

                  <!-- ✅ 右側：台灣時間 -->
                  <div class="log-time">
                    {{ formatTW(l.created_at) }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ========== 分頁五：Prompt 管理（minibazi - 一次看四檔） ========== -->
        <div v-else-if="activeTab === 'prompts'" class="tab-panel">
          <!-- 
          ==========================================================
          ✅ 頂部工具列
          - 一次載入四檔
          - 匯出整包
          ==========================================================
          -->
          <div
            class="toolbar"
            style="justify-content: space-between; gap: 12px"
          >
            <div
              style="
                display: flex;
                gap: 8px;
                align-items: center;
                flex-wrap: wrap;
              "
            >
              <button
                class="ghost-btn"
                @click="fetchMiniBaziPrompts"
                :disabled="promptLoading"
              >
                {{ promptLoading ? "載入中..." : "重新載入四檔" }}
              </button>

              <span style="font-size: 12px; color: #666">
                目前：MiniBazi（4 檔）
              </span>

              <span v-if="promptError" class="error" style="margin-left: 8px">
                {{ promptError }}
              </span>
            </div>

            <div
              style="
                display: flex;
                gap: 8px;
                align-items: center;
                flex-wrap: wrap;
              "
            >
              <button
                class="ghost-btn"
                @click="exportMiniBaziPrompts"
                :disabled="promptLoading"
              >
                匯出整包
              </button>
            </div>
          </div>

          <!-- 
  ==========================================================
  ✅ 四檔同畫面：每檔一張卡
  - 左：textarea
  - 右：操作按鈕 + 備份列表
  ==========================================================
  -->
          <div class="prompts-grid">
            <div v-for="f in promptFilesList" :key="f" class="prompt-card">
              <!-- ✅ 卡片標題 -->
              <div class="prompt-card-header">
                <div>
                  <div class="prompt-filename">{{ f }}</div>
                  <div class="prompt-filetype">
                    類型：<strong>{{ promptTypeOf(f) }}</strong>
                  </div>
                </div>

                <!-- ✅ 右上角：顯示最新備份 id -->
                <div
                  v-if="promptLastSavedBackupIdMap[f]"
                  class="prompt-lastbackup"
                >
                  ✅ 最新備份：<strong>{{
                    promptLastSavedBackupIdMap[f]
                  }}</strong>
                </div>
              </div>

              <div class="prompt-card-body">
                <!-- ✅ 左：textarea -->
                <div class="prompt-editor-col">
                  <textarea
                    v-model="promptEditorTextMap[f]"
                    class="prompt-editor"
                    spellcheck="false"
                    :placeholder="`在這裡編輯：${f}`"
                  ></textarea>
                </div>

                <!-- ✅ 右：功能按鈕 + note + 備份 -->
                <div class="prompt-actions-col">
                  <!-- ✅ note（每檔一個） -->
                  <label class="field" style="margin: 0">
                    版本註記（可選）：
                    <input
                      v-model.trim="promptSaveNoteMap[f]"
                      type="text"
                      placeholder="例如：改年度語氣、縮短 howto"
                    />
                  </label>

                  <div class="prompt-actions">
                    <button
                      @click="saveMiniBaziPromptFileByName(f)"
                      :disabled="promptSavingMap[f] || promptLoading"
                    >
                      {{ promptSavingMap[f] ? "保存中..." : "保存" }}
                    </button>

                    <button
                      class="ghost-btn"
                      @click="reloadOneEditorFromCache(f)"
                      :disabled="promptLoading"
                    >
                      放棄變更
                    </button>

                    <button
                      class="ghost-btn"
                      @click="reloadOneFileFromServer(f)"
                      :disabled="promptLoading"
                    >
                      刷新此檔
                    </button>

                    <!-- ✅ 匯出單檔（下載目前 textarea 內容） -->
                    <button
                      class="ghost-btn"
                      @click="exportOneFileFromEditor(f)"
                    >
                      匯出此檔
                    </button>
                  </div>

                  <!-- ✅ 備份列表 -->
                  <div class="prompt-backups">
                    <div class="prompt-backups-header">
                      <strong>備份版本</strong>
                      <button
                        class="ghost-btn"
                        style="padding: 4px 8px; font-size: 12px"
                        @click="fetchPromptBackupsByName(f)"
                        :disabled="promptBackupsLoadingMap[f]"
                      >
                        {{ promptBackupsLoadingMap[f] ? "載入中..." : "刷新" }}
                      </button>
                    </div>

                    <div v-if="promptBackupsLoadingMap[f]">
                      <em>載入備份中...</em>
                    </div>

                    <div
                      v-else-if="
                        !promptBackupsMap[f] || promptBackupsMap[f].length === 0
                      "
                    >
                      <em>目前沒有備份</em>
                    </div>

                    <div v-else class="prompt-backups-list">
                      <div
                        v-for="b in promptBackupsMap[f]"
                        :key="b.id"
                        class="prompt-backup-row"
                      >
                        <div class="prompt-backup-id">{{ b.id }}</div>
                        <button
                          class="ghost-btn"
                          style="padding: 4px 8px; font-size: 12px"
                          @click="downloadPromptBackupByName(f, b.id)"
                        >
                          下載
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- /body -->
            </div>
            <!-- /card -->
          </div>
          <!-- /grid -->
        </div>
        <!-- ✅ 分頁六：文章管理（已拆出去） -->
        <ArticlesPanel
          v-if="activeTab === 'articles'"
          :apiBaseUrl="cfg.apiBaseUrl"
          :adminToken="adminToken"
        />

        <!-- ✅ 分頁七：八字審定資料（已拆出去） -->
        <MiniBaziAdvicePanel
          v-if="activeTab === 'minibazi_advice'"
          :apiBaseUrl="cfg.apiBaseUrl"
          :adminToken="adminToken"
        />

        <!-- ✅ 分頁八：六爻卜卦紀錄（已拆出去） -->
        <LiuYaoRecordsPanel
          v-if="activeTab === 'liuyao_records'"
          :apiBaseUrl="cfg.apiBaseUrl"
          :adminToken="adminToken"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { adminConfig } from "../admin.config";
import { useAdminAuth } from "../composables/useAdminAuth";
import { useAdminApi } from "../composables/useAdminApi";
import { buildCalendarWeeks } from "../composables/useCalendar";
/* =========================
  ✅ 引入文章管理元件（搬家後）
========================= */
import ArticlesPanel from "./ArticlesPanel.vue";
import MiniBaziAdvicePanel from "./MiniBaziAdvicePanel.vue";
import LiuYaoRecordsPanel from "./LiuYaoRecordsPanel.vue";
import UnavailableCalendarPanel from "./UnavailableCalendarPanel.vue";

const cfg = adminConfig;

export default {
  name: "AdminPanel",

  data() {
    // ✅ 在 Options API 裡先初始化一次，後面 methods 就能用 this.api / this.auth
    const auth = useAdminAuth(cfg);
    const api = useAdminApi(cfg, auth);

    return {
      cfg,
      auth,
      api,

      // ===== 共用登入狀態 =====
      adminToken: auth.getToken() || "", // ✅ 會自動帶上次登入 token
      loggedIn: false,
      loginError: "",
      activeTab: "unavailable",

      // ===== 不開放設定 =====
      unavailable: { fullDay: [], slots: [] },
      unavailableDirty: false, // 月曆格有未儲存變更（切分頁前提醒）
      selectedDate: "", // 預約管理分頁的行事曆選取日

      // ===== 預約管理 =====
      bookings: [],
      loadingBookings: false,

      /* 
      ==========================================
      user_access 管理（列表狀態）
      ==========================================
      */
      userAccessLoading: false,
      userAccessPage: 1,
      userAccessPageSize: 10,
      userAccessQ: "",
      userAccessItems: [],
      userAccessTotal: 0,

      /* 
      ===========================================
        user_access - 編輯狀態
      ===========================================
        ✅ 目的：
        - 只允許一次編輯一列（簡單、穩）
        - editDraft 存「可修改的暫存值」
      */
      userAccessEditingId: "",
      userAccessSaving: false,
      editDraft: {
        first_free: { liuyao: 0, minibazi: 0, bazimatch: 0 },
        quota: { liuyao: 0, minibazi: 0, bazimatch: 0 },
      },

      /* 
      ==========================================================
      ✅ 系統日誌（admin_logs）狀態（先放資料結構）
      - 目的：下一步串 API 時，資料有地方放
      - 目前先不抓資料、不影響既有功能
      ==========================================================
      */
      logsItems: [], // ✅ logs 列表資料（之後放後端回傳的 items）
      logsLoading: false, // ✅ 是否載入中（之後用來顯示「載入中...」）
      logsError: "", // ✅ 失敗訊息（之後顯示在畫面上）

      /* ✅ 分頁資訊 */
      logsPage: 1, // ✅ 目前頁碼
      logsPageSize: 200, // ✅ 每頁筆數
      logsTotal: 0, // ✅ 總筆數（後端回 total）

      // ===== 行事曆用 =====
      calendarYear: new Date().getFullYear(),
      calendarMonth: new Date().getMonth() + 1,
      todayStr: new Date().toISOString().slice(0, 10),

      /* 
      ==========================================================
      ✅ Prompt 管理（minibazi）狀態 - 四檔同畫面
      ==========================================================
      */
      promptLoading: false,
      promptError: "",

      /* ✅ 後端回來的四檔 cache */
      promptFiles: {}, // { filename: { type: "json"|"text", content: any } }

      /* ✅ 固定四檔清單 */
      promptFilesList: [
        "minibazi.json",
        "minibazi.userTemplate.txt",
        "minibazi.howto.txt",
        "minibazi.modeCopy.json",
      ],

      /* ✅ 四個 textarea 的內容（key=filename） */
      promptEditorTextMap: {
        "minibazi.json": "",
        "minibazi.userTemplate.txt": "",
        "minibazi.howto.txt": "",
        "minibazi.modeCopy.json": "",
      },

      /* ✅ 每檔各自的保存 note（key=filename） */
      promptSaveNoteMap: {
        "minibazi.json": "",
        "minibazi.userTemplate.txt": "",
        "minibazi.howto.txt": "",
        "minibazi.modeCopy.json": "",
      },

      /* ✅ 每檔各自的 saving 狀態 */
      promptSavingMap: {
        "minibazi.json": false,
        "minibazi.userTemplate.txt": false,
        "minibazi.howto.txt": false,
        "minibazi.modeCopy.json": false,
      },

      /* ✅ 每檔保存後回來的最新備份 id */
      promptLastSavedBackupIdMap: {
        "minibazi.json": "",
        "minibazi.userTemplate.txt": "",
        "minibazi.howto.txt": "",
        "minibazi.modeCopy.json": "",
      },

      /* ✅ 每檔備份列表（key=filename） */
      promptBackupsMap: {
        "minibazi.json": [],
        "minibazi.userTemplate.txt": [],
        "minibazi.howto.txt": [],
        "minibazi.modeCopy.json": [],
      },

      /* ✅ 每檔備份載入中狀態 */
      promptBackupsLoadingMap: {
        "minibazi.json": false,
        "minibazi.userTemplate.txt": false,
        "minibazi.howto.txt": false,
        "minibazi.modeCopy.json": false,
      },
    };
  },

  /* =========================================================
        ✅ 讓 template 可以用 <EditorContent />
      ========================================================= */
  components: {
    ArticlesPanel,
    MiniBaziAdvicePanel,
    LiuYaoRecordsPanel,
    UnavailableCalendarPanel,
  },

  computed: {
    // ✅ 用抽出的 calendar helper（你原本那坨邏輯乾掉）
    calendarWeeks() {
      return buildCalendarWeeks({
        year: this.calendarYear,
        month: this.calendarMonth,
        bookings: this.bookings,
        unavailable: this.unavailable,
      });
    },

    bookingsForSelectedDate() {
      if (!this.selectedDate) return this.bookings;
      return this.bookings.filter((b) => b.date === this.selectedDate);
    },

    // ✅ 模板化：時段從 config 來
    allTimeSlots() {
      return this.cfg.unavailable.allTimeSlots || [];
    },

    // ✅ 模板化：服務從 config 來
    services() {
      return this.cfg.bookings.services || [];
    },

    /* 
      ==========================================
      user_access - 分頁狀態
      ==========================================
      ✅ 目的：
      - 判斷「下一頁」該不該鎖住
    */
    isUserAccessLastPage() {
      const total = Number(this.userAccessTotal || 0);
      const size = Number(this.userAccessPageSize || 10);
      const page = Number(this.userAccessPage || 1);

      /* 
    ✅ lastPage = ceil(total / pageSize)
    - total=0 時，視為只有第 1 頁
  */
      const lastPage = Math.max(1, Math.ceil(total / size));
      return page >= lastPage;
    },

    /* 
      ==========================================
      user_access - 計算總頁數
      ==========================================
      ✅ 目的：
      - 讓 UI 顯示「第 X 頁 / 共 Y 頁」
      - Y = ceil(total / pageSize)
    */
    userAccessLastPage() {
      const total = Number(this.userAccessTotal || 0);
      const size = Number(this.userAccessPageSize || 10);

      /* 
        ✅ total=0 時，仍視為 1 頁（避免顯示 0 頁）
      */
      return Math.max(1, Math.ceil(total / size));
    },
  },

  methods: {
    // ===== 共用：登入 =====
    async login() {
      this.loginError = "";
      try {
        // ✅ 用 verifyToken 測 token（MOCK = true 時會直接回假 unavailable）
        const unavailable = await this.api.verifyToken(this.adminToken);

        this.unavailable = unavailable || { fullDay: [], slots: [] };
        this.loggedIn = true;

        // ✅ 記住 token，刷新頁面不用再輸入
        this.auth.setToken(this.adminToken);

        // 登入後順便載入預約列表
        await this.fetchBookings();
      } catch (err) {
        console.error(err);
        this.loginError = "登入失敗，請確認密碼是否正確。";
      }
    },

    logout() {
      this.auth.clearToken();
      this.adminToken = "";
      this.loggedIn = false;
      this.activeTab = "unavailable";
      this.unavailableDirty = false;
      this.selectedDate = "";
      this.bookings = [];
    },

    // ===== 分頁切換：不開放設定有未儲存變更時先確認 =====
    // 回傳 true 代表已切換（讓 @click 可接著做 fetch）
    switchTab(tab) {
      if (
        this.activeTab === "unavailable" &&
        tab !== "unavailable" &&
        this.unavailableDirty &&
        !confirm("不開放設定有未儲存的變更，確定離開？（變更會遺失）")
      ) {
        return false;
      }
      this.activeTab = tab;
      return true;
    },

    // ===== 不開放設定相關（月曆格由 UnavailableCalendarPanel 組好整包送來）=====
    async saveUnavailableToServer(updated) {
      try {
        const result = await this.api.saveUnavailable(this.adminToken, updated);

        // ✅ 兼容：後端回 {success:true} 或任何內容都算成功（你也可以改嚴格）
        if (result && result.success === false) throw new Error("儲存失敗");

        this.unavailable = updated;
        alert("已儲存不開放設定");
      } catch (err) {
        console.error(err);
        alert("儲存時發生錯誤，請稍後再試。");
      }
    },

    // ===== 預約管理相關 =====
    async fetchBookings() {
      this.loadingBookings = true;
      try {
        const data = await this.api.listBookings(this.adminToken);
        this.bookings = Array.isArray(data) ? data : [];
      } catch (err) {
        console.error(err);
        alert("載入預約列表時發生錯誤");
      } finally {
        this.loadingBookings = false;
      }
    },

    /* 
      ==========================================
      user_access - 取得列表（先用直連 fetch 驗證）
      ==========================================
      ✅ 為什麼先這樣做：
      - 你目前報「載入失敗」但不知道是 401/404/500
      - 先用最直的 fetch（跟你原本 bookings 一樣）
      - 確認 API 能回、token 有帶到、URL 沒拼錯
      - 等確定通了，再回頭把它抽進 useAdminApi（模板化）
    */
    async fetchUserAccessList() {
      this.userAccessLoading = true;

      try {
        /* 
      ✅ 組 query string（支援分頁 / 搜尋）
    */
        const qs = new URLSearchParams();
        qs.set("page", String(this.userAccessPage || 1));
        qs.set("pageSize", String(this.userAccessPageSize || 200));
        if (this.userAccessQ) qs.set("q", this.userAccessQ);

        /* 
      ✅ 直接拼完整 URL
      - 你現在的 API_BASE_URL 會是：https://api.chen-yi.tw/booking
      - 所以這裡會變成：https://api.chen-yi.tw/booking/api/admin/user-access?... 
    */
        const url = `${cfg.apiBaseUrl}/api/admin/user-access?${qs.toString()}`;

        /* 
      ✅ 發送 request
      - 一定要帶 x-admin-token
    */
        const res = await fetch(url, {
          method: "GET",
          headers: {
            "x-admin-token": this.adminToken,
          },
        });

        /* 
      ✅ 讀 body（不管成功失敗都先讀出來）
      - 這樣你就能看到後端回的 error 訊息
    */
        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        /* 
      ✅ Debug：把 status / url / body 印出來
      - 你只要看 console 就知道是哪一種錯（401/404/500）
    */
        console.log("[UserAccess] GET", { url, status: res.status, data });

        if (!res.ok) {
          /* 
        ✅ 讓你 alert 顯示明確狀態碼
      */
          throw new Error(`HTTP ${res.status}`);
        }

        /* 
      ✅ 後端回傳格式：{ items, total, page, pageSize }
    */
        this.userAccessItems = Array.isArray(data.items) ? data.items : [];
        this.userAccessTotal = Number(data.total || 0);
      } catch (err) {
        console.error("[UserAccess] fetch failed:", err);
        alert(`載入 user_access 列表失敗：${err.message || err}`);
      } finally {
        this.userAccessLoading = false;
      }
    },

    /* 
      ==========================================
      user_access - 搜尋
      ==========================================
      ✅ 做法：
      - 搜尋時回到第 1 頁
      - 重新抓列表
    */
    onUserAccessSearch() {
      this.userAccessPage = 1;
      this.fetchUserAccessList();
    },

    /* 
      ✅ 清除搜尋
      - 把 q 清空
      - 回到第 1 頁
    */
    clearUserAccessSearch() {
      this.userAccessQ = "";
      this.userAccessPage = 1;
      this.fetchUserAccessList();
    },

    /* 
      ==========================================
      user_access - 進入編輯模式
      ==========================================
      ✅ 目的：
      - 把該列目前的值複製到 editDraft
      - 讓 input 綁 editDraft，不直接改原始資料
    */
    startEditUserAccess(u) {
      this.userAccessEditingId = u.user_id;

      this.editDraft = {
        first_free: {
          liuyao: this.getNum(u.first_free, "liuyao"),
          minibazi: this.getNum(u.first_free, "minibazi"),
          bazimatch: this.getNum(u.first_free, "bazimatch"),
        },
        quota: {
          liuyao: this.getNum(u.quota, "liuyao"),
          minibazi: this.getNum(u.quota, "minibazi"),
          bazimatch: this.getNum(u.quota, "bazimatch"),
        },
      };
    },

    /* 
      ==========================================
      user_access - 取消編輯
      ==========================================
      ✅ 目的：
      - 退出編輯模式
      - 不送 API
    */
    cancelEditUserAccess() {
      this.userAccessEditingId = "";
      this.userAccessSaving = false;
    },

    /* 
      ==========================================
      user_access - 產出 PATCH payload（只送你要改的兩包）
      ==========================================
      ✅ 目的：
      - 保持 payload 結構乾淨：{ first_free: {...}, quota: {...} }
      - 確保數值是整數
    */
    buildUserAccessPatchPayload() {
      const toInt = (v) => {
        const n = Number(v);
        /* ✅ 不允許 NaN，預設回 0 */
        if (!Number.isFinite(n)) return 0;
        /* ✅ 這裡統一用整數（你 DB 也是 int） */
        return Math.trunc(n);
      };

      return {
        first_free: {
          liuyao: toInt(this.editDraft.first_free.liuyao),
          minibazi: toInt(this.editDraft.first_free.minibazi),
          bazimatch: toInt(this.editDraft.first_free.bazimatch),
        },
        quota: {
          liuyao: toInt(this.editDraft.quota.liuyao),
          minibazi: toInt(this.editDraft.quota.minibazi),
          bazimatch: toInt(this.editDraft.quota.bazimatch),
        },
      };
    },

    /* 
      ==========================================
      user_access - 保存（PATCH）
      ==========================================
      ✅ 目的：
      - 呼叫後端 PATCH
      - 成功後刷新列表（簡單且保證一致）
    */
    async saveEditUserAccess() {
      if (!this.userAccessEditingId) return;
      if (this.userAccessSaving) return;

      this.userAccessSaving = true;

      try {
        const payload = this.buildUserAccessPatchPayload();

        /* 
          ✅ 直接 fetch（跟你之前 list 一樣）
          - 等這套穩了，再抽回 useAdminApi
        */
        const url = `${
          cfg.apiBaseUrl
        }/api/admin/user-access/${encodeURIComponent(
          this.userAccessEditingId,
        )}`;

        const res = await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "x-admin-token": this.adminToken,
          },
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        console.log("[UserAccess] PATCH", {
          url,
          status: res.status,
          payload,
          data,
        });

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        /* ✅ 保存成功：退出編輯 + 重新抓列表 */
        this.cancelEditUserAccess();
        await this.fetchUserAccessList(); //這個會重新打一次 list API
        alert("已更新 user_access");
      } catch (err) {
        console.error(err);
        alert(`更新失敗：${err.message || err}`);
      } finally {
        this.userAccessSaving = false;
      }
    },

    /* 
      ==========================================
      user_access - 刪除整筆（以 user_id）
      ==========================================
      ✅ 目的：
      - 用 user_id 當 key 做整筆刪除
      - 成功後只做「局部更新」：從 userAccessItems 移除
    */
    async deleteUserAccess(userId) {
      const id = String(userId || "").trim();
      if (!id) return;

      /* 
        ✅ 防呆：如果正在編輯同一筆，先擋下來
        - 避免「編輯中又刪除」造成 UI 狀態亂掉
      */
      if (this.userAccessEditingId === id) {
        alert("此筆正在編輯中，請先保存或取消後再刪除。");
        return;
      }

      /* 
        ✅ 二次確認：避免誤刪
      */
      if (!confirm(`確定要刪除 user_id = ${id} 這整筆資料嗎？`)) return;

      try {
        const url = `${
          cfg.apiBaseUrl
        }/api/admin/user-access/${encodeURIComponent(id)}`;

        const res = await fetch(url, {
          method: "DELETE",
          headers: {
            "x-admin-token": this.adminToken,
          },
        });

        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        console.log("[UserAccess] DELETE", { url, status: res.status, data });

        /* 
          ✅ 200：刪除成功
          ✅ 404：可能已被刪掉（你剛說會回 404），我們也當作完成，直接把 UI 移除
        */
        if (res.status !== 200 && res.status !== 404) {
          throw new Error(`HTTP ${res.status}`);
        }

        /* 
          ✅ 局部更新：從列表移除該筆
        */
        this.userAccessItems = this.userAccessItems.filter(
          (x) => x.user_id !== id,
        );

        /* 
          ✅ total 也同步扣 1（避免分頁資訊怪掉）
          - 若 total 已經是 0 就不扣
        */
        if (Number(this.userAccessTotal || 0) > 0) {
          this.userAccessTotal = Number(this.userAccessTotal) - 1;
        }

        /* 
          ✅ 如果你刪到這一頁最後一筆，且不是第 1 頁
          - 讓它自動退一頁再載入（可選但很實用）
        */
        if (this.userAccessItems.length === 0 && this.userAccessPage > 1) {
          this.userAccessPage -= 1;
          await this.fetchUserAccessList(); /* ✅ 這裡重抓一次是為了填滿該頁 */
        }

        alert(res.status === 404 ? "此筆可能已被刪除（已更新畫面）" : "已刪除");
      } catch (err) {
        console.error(err);
        alert(`刪除失敗：${err.message || err}`);
      }
    },

    /* 
      ==========================================
      user_access - 分頁
      ==========================================
    */
    prevUserAccessPage() {
      if (this.userAccessPage <= 1) return;
      this.userAccessPage -= 1;
      this.fetchUserAccessList();
    },

    nextUserAccessPage() {
      if (this.isUserAccessLastPage) return;
      this.userAccessPage += 1;
      this.fetchUserAccessList();
    },

    /* 
      ==========================================
      user_access - 安全數字取值
      ==========================================
      ✅ 目的：
      - 避免 first_free/quota 欄位為 null / undefined 時報錯
      - 避免值不是數字時顯示 NaN
    */
    getNum(obj, key) {
      const n = Number(obj && obj[key] != null ? obj[key] : 0);
      return Number.isFinite(n) ? n : 0;
    },

    /* 
    ==========================================================
    ✅ Logs：抓後端 admin_logs 列表
    - 目的：先確認 API 能通、token 正確、資料能回來
    - 先用最少參數：page/pageSize
    ==========================================================
    */
    async fetchLogsList() {
      this.logsLoading = true;
      this.logsError = "";

      try {
        /* 
    ✅ 組 query string（先只有 page / pageSize）
    - 之後要加搜尋/篩選再擴充
    */
        const params = new URLSearchParams();
        params.set("page", String(this.logsPage));
        params.set("pageSize", String(this.logsPageSize));

        /* ✅ 呼叫後端 */
        const res = await fetch(
          `${cfg.apiBaseUrl}/api/admin/logs?${params.toString()}`,
          {
            headers: {
              "x-admin-token": this.adminToken,
            },
          },
        );

        /* ✅ 先把 HTTP 錯誤變成可讀的訊息 */
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        /* 
        ✅ 依你後端格式：{ items, total, page, pageSize }
        - items: 陣列
        - total: 總筆數
        */
        const data = await res.json();
        this.logsItems = Array.isArray(data.items) ? data.items : [];
        this.logsTotal = Number(data.total || 0);
        this.logsPage = Number(data.page || this.logsPage);
        this.logsPageSize = Number(data.pageSize || this.logsPageSize);
      } catch (err) {
        console.error("[fetchLogsList] error:", err);
        this.logsError = `載入 logs 失敗：${err.message || err}`;
      } finally {
        this.logsLoading = false;
      }
    },

    /* 
    ==========================================================
    ✅ Logs：把 created_at 轉成台灣時間顯示
    - 目的：你看 log 才不會一直在 +00:00 猜時間
    - 做法：前端用 Asia/Taipei 轉換（不動 DB、不動後端）
    ==========================================================
    */
    formatTW(iso) {
      if (!iso) return "-";
      try {
        return new Date(iso).toLocaleString("zh-TW", {
          timeZone: "Asia/Taipei",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        });
      } catch {
        return iso;
      }
    },

    /* 
    ==========================================================
    ✅ Prompt 工具：取得檔案類型
    ==========================================================
    */
    promptTypeOf(filename) {
      const entry = this.promptFiles[filename];
      return entry && entry.type ? entry.type : "-";
    },

    /* 
    ==========================================================
    ✅ Prompt：把後端 cache 套到四個 textarea
    - json -> pretty JSON
    - text -> 原樣字串
    ==========================================================
    */
    applyAllPromptsToEditors() {
      for (const f of this.promptFilesList) {
        const entry = this.promptFiles[f];

        if (!entry) {
          this.promptEditorTextMap[f] = "";
          continue;
        }

        if (entry.type === "json") {
          try {
            this.promptEditorTextMap[f] = JSON.stringify(
              entry.content,
              null,
              2,
            );
          } catch {
            this.promptEditorTextMap[f] = String(entry.content ?? "");
          }
        } else {
          this.promptEditorTextMap[f] = String(entry.content ?? "");
        }
      }
    },

    /* 
    ==========================================================
    ✅ 讀四檔：GET /api/admin/prompts/minibazi
    - 讀到後就直接填滿四個 textarea
    ==========================================================
    */
    async fetchMiniBaziPrompts() {
      this.promptLoading = true;
      this.promptError = "";

      try {
        const url = `${cfg.apiBaseUrl}/api/admin/prompts/minibazi`;

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "x-admin-token": this.adminToken,
          },
        });

        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        console.log("[Prompts] GET minibazi", {
          url,
          status: res.status,
          data,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        this.promptFiles = (data && data.files) || {};

        /* ✅ 套用到四個 textarea */
        this.applyAllPromptsToEditors();

        /* ✅ 同步刷新四份備份列表（你想一進來就都看到） */
        for (const f of this.promptFilesList) {
          await this.fetchPromptBackupsByName(f);
        }
      } catch (err) {
        console.error(err);
        this.promptError = `載入 prompt 失敗：${err.message || err}`;
      } finally {
        this.promptLoading = false;
      }
    },

    /* 
    ==========================================================
    ✅ 放棄變更（單檔）：回到 cache
    ==========================================================
    */
    reloadOneEditorFromCache(filename) {
      const entry = this.promptFiles[filename];

      /* ✅ note 一併清掉（避免你下一次保存誤帶） */
      this.promptSaveNoteMap[filename] = "";
      this.promptLastSavedBackupIdMap[filename] = "";

      if (!entry) {
        this.promptEditorTextMap[filename] = "";
        return;
      }

      if (entry.type === "json") {
        try {
          this.promptEditorTextMap[filename] = JSON.stringify(
            entry.content,
            null,
            2,
          );
        } catch {
          this.promptEditorTextMap[filename] = String(entry.content ?? "");
        }
      } else {
        this.promptEditorTextMap[filename] = String(entry.content ?? "");
      }
    },

    /* 
    ==========================================================
    ✅ 刷新此檔（單檔）
    - 最小做法：直接重抓四檔，再把其中一檔 textarea 套回來
    - 原因：你後端目前只有「讀整包」API（沒有單檔 read API）
    ==========================================================
    */
    async reloadOneFileFromServer(filename) {
      await this.fetchMiniBaziPrompts();
      this.reloadOneEditorFromCache(filename);
    },

    /* 
    ==========================================================
    ✅ 保存單檔：PUT /api/admin/prompts/minibazi
    - body: { filename, content, note }
    - json 類型：textarea 必須能 JSON.parse
    ==========================================================
    */
    async saveMiniBaziPromptFileByName(filename) {
      if (this.promptSavingMap[filename]) return;

      this.promptSavingMap[filename] = true;
      this.promptError = "";
      this.promptLastSavedBackupIdMap[filename] = "";

      try {
        const entry = this.promptFiles[filename];
        if (!entry) throw new Error("找不到檔案（請先載入四檔）");

        let contentToSave = null;

        if (entry.type === "json") {
          try {
            contentToSave = JSON.parse(this.promptEditorTextMap[filename]);
          } catch {
            throw new Error("JSON 格式錯誤：請確認逗號/括號/雙引號是否正確");
          }
        } else {
          contentToSave = String(this.promptEditorTextMap[filename] ?? "");
        }

        const payload = {
          filename,
          content: contentToSave,
          note: this.promptSaveNoteMap[filename] || "",
        };

        const url = `${cfg.apiBaseUrl}/api/admin/prompts/minibazi`;

        const res = await fetch(url, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "x-admin-token": this.adminToken,
          },
          body: JSON.stringify(payload),
        });

        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        console.log("[Prompts] PUT", {
          url,
          status: res.status,
          filename,
          data,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        /* ✅ 更新前端 cache：保存成功後 cache 要跟 textarea 同步 */
        this.promptFiles[filename] = {
          type: entry.type,
          content: contentToSave,
        };

        /* ✅ 記下本次備份 id（後端回 backupId） */
        this.promptLastSavedBackupIdMap[filename] =
          data && data.backupId ? data.backupId : "";

        /* ✅ 保存完刷新該檔備份列表 */
        await this.fetchPromptBackupsByName(filename);

        alert(`已保存：${filename}（並自動備份）`);
      } catch (err) {
        console.error(err);
        this.promptError = `保存失敗（${filename}）：${err.message || err}`;
        alert(this.promptError);
      } finally {
        this.promptSavingMap[filename] = false;
      }
    },

    /* 
    ==========================================================
    ✅ 匯出整包（下載 json）
    - GET /api/admin/prompts/minibazi/export
    ==========================================================
    */
    async exportMiniBaziPrompts() {
      this.promptError = "";

      try {
        const url = `${cfg.apiBaseUrl}/api/admin/prompts/minibazi/export`;

        const res = await fetch(url, {
          method: "GET",
          headers: { "x-admin-token": this.adminToken },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const cd = res.headers.get("content-disposition") || "";
        const m = cd.match(/filename="([^"]+)"/);
        const filename = m ? m[1] : "minibazi_prompts_export.json";

        const blob = await res.blob();
        this.downloadBlob(blob, filename);
      } catch (err) {
        console.error(err);
        this.promptError = `匯出失敗：${err.message || err}`;
        alert(this.promptError);
      }
    },

    /* 
    ==========================================================
    ✅ 匯出單檔（下載目前 textarea 內容）
    - 目的：你改完但不一定要保存，也能先備份到本機
    - 做法：前端直接把 textarea 內容打包成 blob 下載
    ==========================================================
    */
    exportOneFileFromEditor(filename) {
      try {
        const entry = this.promptFiles[filename];
        const type = entry && entry.type ? entry.type : "text";

        const ts = new Date()
          .toISOString()
          .replace(/[:.]/g, "-")
          .replace("T", "_")
          .replace("Z", "");
        const safeName = `${filename}__${ts}`;

        const contentText = String(this.promptEditorTextMap[filename] ?? "");

        const blob = new Blob([contentText], {
          type:
            type === "json"
              ? "application/json;charset=utf-8"
              : "text/plain;charset=utf-8",
        });

        /* ✅ 檔名：保持副檔名不變 */
        this.downloadBlob(blob, safeName);
      } catch (err) {
        console.error(err);
        alert(`匯出單檔失敗：${err.message || err}`);
      }
    },

    /* 
    ==========================================================
    ✅ 列備份（單檔）：GET /api/admin/prompts/backups?filename=xxx
    ==========================================================
    */
    async fetchPromptBackupsByName(filename) {
      this.promptBackupsLoadingMap[filename] = true;

      try {
        const qs = new URLSearchParams();
        qs.set("filename", filename);

        const url = `${
          cfg.apiBaseUrl
        }/api/admin/prompts/backups?${qs.toString()}`;

        const res = await fetch(url, {
          method: "GET",
          headers: { "x-admin-token": this.adminToken },
        });

        const text = await res.text();
        let data = null;
        try {
          data = JSON.parse(text);
        } catch {
          data = text;
        }

        console.log("[Prompts] GET backups", {
          filename,
          status: res.status,
          data,
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        this.promptBackupsMap[filename] = Array.isArray(data.items)
          ? data.items
          : [];
      } catch (err) {
        console.error(err);
        /* ✅ 備份載入失敗不應該把整頁打爆，所以只記錄，不 alert */
        this.promptBackupsMap[filename] = [];
      } finally {
        this.promptBackupsLoadingMap[filename] = false;
      }
    },

    /* 
    ==========================================================
    ✅ 下載備份（單檔）
    - GET /api/admin/prompts/backups/download?filename=xxx&id=yyy
    ==========================================================
    */
    async downloadPromptBackupByName(filename, backupId) {
      this.promptError = "";

      try {
        const qs = new URLSearchParams();
        qs.set("filename", filename);
        qs.set("id", backupId);

        const url = `${
          cfg.apiBaseUrl
        }/api/admin/prompts/backups/download?${qs.toString()}`;

        const res = await fetch(url, {
          method: "GET",
          headers: { "x-admin-token": this.adminToken },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const blob = await res.blob();
        this.downloadBlob(blob, backupId);
      } catch (err) {
        console.error(err);
        this.promptError = `下載備份失敗：${err.message || err}`;
        alert(this.promptError);
      }
    },

    /* 
    ==========================================================
    ✅ 小工具：下載 blob（共用）
    ==========================================================
    */
    downloadBlob(blob, filename) {
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "download";
      document.body.appendChild(a);
      a.click();
      a.remove();

      URL.revokeObjectURL(url);
    },

    // ===== 行事曆相關 =====
    prevMonth() {
      if (this.calendarMonth === 1) {
        this.calendarMonth = 12;
        this.calendarYear -= 1;
      } else {
        this.calendarMonth -= 1;
      }
    },

    nextMonth() {
      if (this.calendarMonth === 12) {
        this.calendarMonth = 1;
        this.calendarYear += 1;
      } else {
        this.calendarMonth += 1;
      }
    },

    selectDateFromCalendar(cell) {
      if (!cell) return;
      this.selectedDate = cell.dateStr;
    },

    normalizeSlots(booking) {
      if (Array.isArray(booking.timeSlots)) return booking.timeSlots;
      if (booking.timeSlot) return [booking.timeSlot];
      return [];
    },

    serviceTitle(serviceId) {
      const s = this.services.find((s) => s.id === serviceId);
      return s ? s.title : serviceId || "-";
    },

    formatDateTime(iso) {
      if (!iso) return "-";
      try {
        const d = new Date(iso);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        const hh = String(d.getHours()).padStart(2, "0");
        const mi = String(d.getMinutes()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd} ${hh}:${mi}`;
      } catch {
        return iso;
      }
    },

    async updateStatus(booking) {
      try {
        await this.api.updateBookingStatus(
          this.adminToken,
          booking.id,
          booking.status,
        );
      } catch (err) {
        console.error(err);
        alert("更新狀態時發生錯誤");
      }
    },

    async removeBooking(booking) {
      if (!confirm(`確定要刪除 ${booking.name} 的預約嗎？`)) return;

      try {
        await this.api.deleteBooking(this.adminToken, booking.id);
        this.bookings = this.bookings.filter((b) => b.id !== booking.id);
      } catch (err) {
        console.error(err);
        alert("刪除預約時發生錯誤");
      }
    },
  },

  // ✅ 一進來如果 localStorage 有 token，就自動嘗試登入（體驗超加分）
  async mounted() {
    if (!this.adminToken) return;
    try {
      const unavailable = await this.api.verifyToken(this.adminToken);
      this.unavailable = unavailable || { fullDay: [], slots: [] };
      this.loggedIn = true;
      await this.fetchBookings();
    } catch {
      // token 不對就清掉，避免一直卡死
      this.auth.clearToken();
      this.adminToken = "";
    }
  },
};
</script>

<style scoped>
.page {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 60px 16px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
}

.card {
  width: 100%;
  max-width: 1000px;
  background: #ffffff;
  border-radius: 12px;
  padding: 28px 24px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.06);
}

h2 {
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  margin-bottom: 6px;
  color: #333;
}

.subtitle {
  font-size: 14px;
  color: #666;
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.4;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 14px;
  color: #333;
}

input {
  padding: 8px 10px;
  font-size: 14px;
  border-radius: 6px;
  border: 1px solid #ccc;
  outline: none;
}

input:focus {
  border-color: #888;
}

button {
  padding: 8px 12px;
  font-size: 14px;
  cursor: pointer;
  border-radius: 6px;
  border: none;
  background: #333;
  color: #888;
}

button:hover {
  opacity: 0.92;
}

.error {
  color: #d32f2f;
  font-size: 12px;
}

/* Tabs */
.tabs {
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
  margin-bottom: 8px;
}

.tab-btn {
  padding: 6px 12px;
  font-size: 14px;
  border-radius: 999px;
  border: 1px solid transparent;
  background: transparent;
  cursor: pointer;
}

.tab-btn.active {
  background: #333;
  color: #fff;
}

.open-tag {
  background: rgba(25, 118, 210, 0.12);
  color: #1565c0;
}

.blocked-tag {
  background: rgba(198, 40, 40, 0.12);
  color: #b71c1c;
}

/* 預約管理表格 */
.toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.table-wrapper {
  overflow-x: auto;
}

.booking-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.booking-table th,
.booking-table td {
  border: 1px solid #eee;
  padding: 6px 8px;
  text-align: left;
  vertical-align: top;
}

.booking-table thead {
  background: #f5f5f5;
}

.timechips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.chip {
  padding: 2px 6px;
  border-radius: 999px;
  background: #e3f2fd;
  color: #1565c0;
  font-size: 11px;
}

.status-select {
  font-size: 12px;
  padding: 4px 6px;
}

.danger-btn {
  background: #c62828;
  font-size: 12px;
  padding: 6px 10px;
}

.danger-btn:hover {
  background: #b71c1c;
}

.contact-cell {
  max-width: 180px;
  white-space: pre-wrap;
  word-break: break-word;
}
/*=====行事曆樣式======*/
.calendar-panel {
  margin-bottom: 16px;
  border-radius: 8px;
  background: #fafafa;
  padding: 12px;
  border: 1px solid #eee;
}

.calendar-header {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.calendar-header button {
  padding: 4px 8px;
  font-size: 14px;
}

.calendar-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  text-align: center;
}

.calendar-table th,
.calendar-table td {
  border: 1px solid #eee;
  width: 14.28%;
  height: 52px;
  padding: 0;
}

.calendar-cell {
  cursor: pointer;
}

.calendar-cell .cell-inner {
  width: 100%;
  height: 100%;
  padding: 4px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.day-number {
  font-size: 13px;
}

.cell-tags {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
}

.tag {
  font-size: 10px;
  padding: 1px 4px;
  border-radius: 999px;
}

.calendar-cell.is-selected {
  background: #e3f2fd;
}

.calendar-cell.is-today {
  outline: 1px solid #1976d2;
}

.calendar-cell.has-bookings .day-number {
  font-weight: 600;
}

.calendar-cell.is-blocked-day {
  background: #fff3f3;
}

.calendar-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  margin-top: 6px;
  align-items: center;
}

.legend-box {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 4px;
}

.legend-open {
  background: #e3f2fd;
}

.legend-blocked {
  background: #ffcdd2;
}

/* 
  ==========================================
  user_access - 搜尋/分頁樣式
  ==========================================
  ✅ 目的：
  - 讓工具列不擠成一團
*/
.ua-search {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.ua-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.ua-pagination {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: flex-end;
  margin: 10px 0 12px;
}

.ua-pageinfo {
  font-size: 12px;
  color: #555;
}

.ghost-btn {
  background: transparent;
  border: 1px solid #ddd;
  color: #333;
}
.danger-btn {
  background: transparent;
  border: 1px solid #ddd;
  color: #333;
}

.ghost-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 
  ✅ 數字欄位置中，看起來更像後台
*/
.num-cell {
  text-align: center;
  font-variant-numeric: tabular-nums; /* 數字等寬，好對齊 */
}

/* 
  ==========================================
  user_access - 編輯樣式
  ==========================================
  ✅ 目的：
  - input 不要擠爆表格
*/
.num-input {
  width: 70px;
  padding: 6px 8px;
  font-size: 12px;
  border-radius: 6px;
  border: 1px solid #ccc;
  text-align: center;
}

.ops-cell {
  white-space: nowrap;
  display: flex;
  gap: 8px;
  align-items: center;
}

/* 
  ==========================================
  ✅ 強制數字欄置中（提高 specificity）
  ==========================================
  ✅ 為什麼這樣寫：
  - 你原本有 .booking-table td { text-align: left; }
  - 單純 .num-cell 可能被蓋掉
  - 寫成 .booking-table td.num-cell 就一定贏
*/
.booking-table td.num-cell {
  text-align: center;
  font-variant-numeric: tabular-nums; /* 數字等寬，好對齊 */
}

/* 
  ✅（可選）連表頭一起置中，視覺更一致
*/
.booking-table th.num-cell {
  text-align: center;
}

/* 
==========================================================
✅ Logs：聊天室容器（你指定配色）
- 背景：#ecf0f8
- 字體：rgba(94, 93, 93, 0.86)
- 邊框：rgba(255, 255, 255, 0.04)
==========================================================
*/
.logs-chat {
  height: 520px; /* ✅ 你可調 */
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.04); /* ✅ 你指定 */
  border-radius: 10px;
  background: #ecf0f8; /* ✅ 你指定 */
  padding: 10px;
  color: rgba(94, 93, 93, 0.86); /* ✅ 統一字色 */
}

/* ✅ 空狀態 */
.logs-empty {
  color: rgba(94, 93, 93, 0.86); /* ✅ 統一字色 */
  padding: 10px;
}

/* ✅ 每筆 log：一行 */
.log-row {
  display: grid;
  grid-template-columns: 220px 1fr 180px;
  gap: 10px;
  padding: 10px;
  border-radius: 10px;

  /* ✅ 你指定：邊框色 */
  border: 1px solid rgba(255, 255, 255, 0.04);

  /* ✅ 淺色背景上的卡片：用白底 + 一點透明感，讓內容更清楚 */
  background: rgba(255, 255, 255, 0.65);

  /* ✅ 統一字色 */
  color: rgba(94, 93, 93, 0.86);

  margin-bottom: 8px;
}

/* ✅ 左側 meta */
.log-user {
  font-weight: 600;
  font-size: 12px;
  word-break: break-all;
  color: rgba(94, 93, 93, 0.86); /* ✅ 統一字色 */
}

.badge {
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 999px;

  /* ✅ 淺色系 badge */
  background: rgba(255, 255, 255, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.04);

  color: rgba(94, 93, 93, 0.86); /* ✅ 統一字色 */
}

/* ✅ message */
.log-msg {
  font-size: 13px;
  line-height: 1.45;
  white-space: pre-wrap;
  word-break: break-word;
  color: rgba(94, 93, 93, 0.86); /* ✅ 統一字色 */
}
/* 
==========================================================
✅ badge 特化：info / user_text 底色加深一點
- 你目前 badge 是白底偏淡，這裡讓它更「看得出來」
==========================================================
*/
.badge-level-info {
  background: rgba(220, 230, 255, 0.95); /* ✅ 比較深的淡藍 */
}

.badge-tag-user_text {
  background: rgba(210, 235, 220, 0.95); /* ✅ 比較深的淡綠 */
}

/* ✅ time */
.log-time {
  font-size: 12px;
  text-align: right;
  color: rgba(94, 93, 93, 0.86); /* ✅ 統一字色 */
  font-variant-numeric: tabular-nums;
}

/* 
==========================================================
✅ Prompt grid：四檔同畫面
==========================================================
*/
.prompts-grid {
  display: grid;
  grid-template-columns: 1fr; /* ✅ 先一欄，避免太擠 */
  gap: 14px;
}

/* ✅ 卡片 */
.prompt-card {
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fff;
  padding: 14px;
}

/* ✅ header */
.prompt-card-header {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.prompt-filename {
  font-weight: 700;
  font-size: 14px;
  color: #333;
}

.prompt-filetype {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.prompt-lastbackup {
  font-size: 12px;
  color: #666;
}

/* ✅ body：左 editor / 右 actions */
.prompt-card-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 12px;
}

/* ✅ editor */
.prompt-editor {
  width: 100%;
  min-height: 320px;
  min-width: 0;
  padding: 10px 12px;
  font-size: 12px;
  line-height: 1.5;
  border-radius: 10px;
  border: 1px solid #ddd;
  background: #fafafa;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    "Liberation Mono", "Courier New", monospace;
  resize: vertical;
}

/* ✅ 右側 actions */
.prompt-actions-col {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.prompt-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ✅ 備份列表 */
.prompt-backups {
  border-top: 1px solid #eee;
  padding-top: 10px;
}

.prompt-backups-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
}

.prompt-backups-list {
  max-height: 220px;
  overflow: auto;
  border: 1px solid #eee;
  border-radius: 10px;
  background: #fafafa;
  padding: 8px;
}

.prompt-backup-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
  align-items: center;
  padding: 6px 6px;
  border-radius: 8px;
}

.prompt-backup-row:hover {
  background: rgba(0, 0, 0, 0.03);
}

.prompt-backup-id {
  font-size: 12px;
  color: #444;
  word-break: break-all;
}

/* ==========================================================
   ✅ 救火：強制左右欄不要互相擠爆
   為什麼可能你剛那段沒用：
   - .prompt-card-body 根本不是實際的 class
   - 或被 scoped 擋掉（元素在子元件/slot）
   - 或被後面 CSS 覆蓋

   做法：
   - 用 :deep() 確保即使是子元件也吃得到
   - 用 !important 先把版面救回來
   ========================================================== */
:deep(.prompt-card-body) {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) 240px !important; /* ✅ 右欄更窄 */
  gap: 12px !important;
  align-items: start !important;
}

/* ==========================================================
   ✅ 關鍵：左欄要允許縮（不然會把右欄擠到重疊）
   ========================================================== */
:deep(.prompt-card-body > :first-child) {
  min-width: 0 !important;
}

/* ==========================================================
   ✅ textarea 也要允許縮 + 不要撐爆容器
   ========================================================== */
:deep(.prompt-card-body textarea) {
  font-size: 15px; /* 你想更大就 16px / 17px */
  line-height: 1.55; /* 行距更舒服 */
  width: 100% !important;
  min-width: 0 !important;
  box-sizing: border-box !important;
}

/* ==========================================================
   ✅ 右欄固定窄一點，避免被內容撐寬
   ========================================================== */
:deep(.prompt-card-body > :last-child) {
  width: 240px !important;
  max-width: 240px !important;
}

/* ==========================================================
   ✅ Step 1：讓左欄跟著整列長高，textarea 吃滿剩餘空間
   為什麼：
   - grid 這一列高度會被右欄撐高
   - 但左欄內容預設不會自動「撐滿」
   - 所以要：左欄 stretch + 左欄用 flex column + textarea flex:1
   ========================================================== */

/* ✅ 左欄撐滿 grid 的高度，並改成直向 flex 容器 */
:deep(.prompt-card-body > :first-child) {
  align-self: stretch !important; /* 讓左欄高度跟整列一樣高 */
  display: flex !important; /* 用 flex 才能讓 textarea 撐高 */
  flex-direction: column !important; /* 直向堆疊 */
}

/* ✅ textarea 吃掉左欄的剩餘高度 */
:deep(.prompt-card-body > :first-child textarea) {
  flex: 1 1 auto !important; /* 讓 textarea 自動長高 */
  min-height: 420px !important; /* 你想更高就加大：500/600 */
  resize: vertical; /* 仍允許手動拖拉（可留可拔） */
}

/* ✅ RWD：窄螢幕改成上下 */
@media (max-width: 920px) {
  .prompt-card-body {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 860px) {
  .log-row {
    grid-template-columns: 1fr;
  }
  .log-time {
    text-align: left;
  }
}
</style>
